import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { IncrementFor } from 'frontend-utils';
import MarketTotalRevenueKpiItem from 'lumen/components/market/total-revenue/kpi-item';
import type {
  TotalRevenueItem,
  TotalRevenueSegmentationBreakdownSummaryItem,
} from 'lumen/models';
import type { View } from 'lumen/utils/constants/total-revenue-constants';
import safeDivide from 'lumen/utils/numeric/safe-divide';

interface MarketTotalRevenueKpiSectionSignature {
  Args: {
    isLoading?: boolean;
    isPerAvailableRoom: boolean;
    segmentationBreakdownSummary:
      | TotalRevenueSegmentationBreakdownSummaryItem
      | undefined;
    summary: TotalRevenueItem | undefined;
    view: View;
  };
}

export default class MarketTotalRevenueKpiSection extends Component<MarketTotalRevenueKpiSectionSignature> {
  @service declare totalRevenue: Services['total-revenue'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMatchedNonRoomRevenuePercentage() {
    const matchedNonRoomRevenuePercentage = safeDivide(
      this.args.segmentationBreakdownSummary?.matchedNonRoomRevenue,
      this.args.summary?.totalNonRoomRevenue,
    );

    return this.totalRevenue.formatPercentage(matchedNonRoomRevenuePercentage);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedUnmatchedNonRoomRevenuePercentage() {
    const unmatchedNonRoomRevenuePercentage = safeDivide(
      this.args.segmentationBreakdownSummary?.unmatchedNonRoomRevenue,
      this.args.summary?.totalNonRoomRevenue,
    );

    return this.totalRevenue.formatPercentage(
      unmatchedNonRoomRevenuePercentage,
    );
  }

  <template>
    <div class="kpi-section">
      {{#if @isLoading}}
        <IncrementFor @times={{4}}>
          <PrismCard>
            <:header>
              <div class="pt-200 pb-200"><div
                  class="loading-bar w-1/2"
                ></div></div>
            </:header>
            <:content>
              <div class="loading-content flex flex-col justify-between">
                <div class="pt-300 pb-300"><div
                    class="loading-bar w-2/3"
                  ></div></div>
                <div class="pt-50 pb-50">
                  <div class="loading-bar w-1/3"></div></div>
              </div>
            </:content>
          </PrismCard>
        </IncrementFor>
      {{else if @summary}}
        <MarketTotalRevenueKpiItem
          @title={{t
            (if
              @isPerAvailableRoom
              "general.trevpar"
              "total_revenue.total_revenue"
            )
          }}
          @value={{@summary.formattedTotalRevenue}}
          as |K|
        >
          {{#each @summary.sortedReferenceValues as |referenceValue|}}
            <K.ReferenceItem
              @changeValue={{referenceValue.totalRevenueRelativeChange}}
              @formattedChangeValue={{referenceValue.formattedTotalRevenueRelativeChange}}
              @referencePeriod={{referenceValue.translatedReferenceType}}
            />
          {{/each}}
        </MarketTotalRevenueKpiItem>

        {{#if (eq @view "departments")}}
          <MarketTotalRevenueKpiItem
            @subtitle={{t
              "total_revenue.percentage_of_total_revenue"
              value=@summary.formattedTotalRoomRevenuePercentage
            }}
            @title={{t
              (if
                @isPerAvailableRoom
                "general.revpar"
                "total_revenue.room_revenue"
              )
            }}
            @value={{@summary.formattedTotalRoomRevenue}}
            as |K|
          >
            {{#each @summary.sortedReferenceValues as |referenceValue|}}
              <K.ReferenceItem
                @changeValue={{referenceValue.totalRoomRevenueRelativeChange}}
                @formattedChangeValue={{referenceValue.formattedTotalRoomRevenueRelativeChange}}
                @referencePeriod={{referenceValue.translatedReferenceType}}
              />
            {{/each}}
          </MarketTotalRevenueKpiItem>
        {{/if}}

        <MarketTotalRevenueKpiItem
          @subtitle={{t
            "total_revenue.percentage_of_total_revenue"
            value=@summary.formattedTotalNonRoomRevenuePercentage
          }}
          @title={{t
            (if
              @isPerAvailableRoom
              "general.arevpar"
              "total_revenue.non_room_revenue"
            )
          }}
          @value={{@summary.formattedTotalNonRoomRevenue}}
          as |K|
        >
          {{#each @summary.sortedReferenceValues as |referenceValue|}}
            <K.ReferenceItem
              @changeValue={{referenceValue.totalNonRoomRevenueRelativeChange}}
              @formattedChangeValue={{referenceValue.formattedTotalNonRoomRevenueRelativeChange}}
              @referencePeriod={{referenceValue.translatedReferenceType}}
            />
          {{/each}}
        </MarketTotalRevenueKpiItem>

        {{#if (eq @view "departments")}}
          <MarketTotalRevenueKpiItem
            @subtitle={{t "total_revenue.per_100_of_room_revenue"}}
            @title={{t "total_revenue.non_room_premium"}}
            @value={{@summary.formattedNonRoomPremium}}
            as |K|
          >
            {{#each @summary.sortedReferenceValues as |referenceValue|}}
              <K.ReferenceItem
                @changeValue={{referenceValue.nonRoomPremiumRelativeChange}}
                @formattedChangeValue={{referenceValue.formattedNonRoomPremiumRelativeChange}}
                @referencePeriod={{referenceValue.translatedReferenceType}}
              />
            {{/each}}
          </MarketTotalRevenueKpiItem>
        {{/if}}

        {{#if @segmentationBreakdownSummary}}
          {{#if (eq @view "strategy")}}
            <MarketTotalRevenueKpiItem
              @subtitle={{t
                "total_revenue.percentage_of_non_room_revenue"
                value=this.formattedMatchedNonRoomRevenuePercentage
              }}
              @title={{t "total_revenue.matched_non_room_revenue"}}
              @tooltipMessage={{t
                "total_revenue.matched_non_room_revenue.tooltip"
              }}
              @value={{@segmentationBreakdownSummary.formattedMatchedNonRoomRevenue}}
              as |K|
            >
              {{#each
                @segmentationBreakdownSummary.sortedReferenceValues
                as |referenceValue|
              }}
                <K.ReferenceItem
                  @changeValue={{referenceValue.matchedNonRoomRevenueRelativeChange}}
                  @formattedChangeValue={{referenceValue.formattedMatchedNonRoomRevenueRelativeChange}}
                  @referencePeriod={{referenceValue.translatedReferenceType}}
                />
              {{/each}}
            </MarketTotalRevenueKpiItem>
          {{/if}}

          {{#if (eq @view "strategy")}}
            <MarketTotalRevenueKpiItem
              @subtitle={{t
                "total_revenue.percentage_of_non_room_revenue"
                value=this.formattedUnmatchedNonRoomRevenuePercentage
              }}
              @title={{t "total_revenue.unmatched_non_room_revenue"}}
              @tooltipMessage={{t
                "total_revenue.unmatched_non_room_revenue.tooltip"
              }}
              @value={{@segmentationBreakdownSummary.formattedUnmatchedNonRoomRevenue}}
              as |K|
            >
              {{#each
                @segmentationBreakdownSummary.sortedReferenceValues
                as |referenceValue|
              }}
                <K.ReferenceItem
                  @changeValue={{referenceValue.unmatchedNonRoomRevenueRelativeChange}}
                  @formattedChangeValue={{referenceValue.formattedUnmatchedNonRoomRevenueRelativeChange}}
                  @referencePeriod={{referenceValue.translatedReferenceType}}
                />
              {{/each}}
            </MarketTotalRevenueKpiItem>
          {{/if}}
        {{/if}}
      {{/if}}
    </div>
  </template>
}
